/**
 * Public Holidays Management Page - Admin/HR can manage public holidays
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import toast from 'react-hot-toast'
import { leaveApi } from '../api/leaveApi'
import type { PublicHoliday, CreatePublicHolidayInput, UpdatePublicHolidayInput, BulkCreatePublicHolidayInput } from '../types/leave.types'
import LoadingPage from '@/shared/components/LoadingPage'

interface HolidayFormData {
  name: string
  date: string
  end_date: string
  description: string
  is_recurring: boolean
  is_active: boolean
  isRange: boolean
}

const emptyFormData: HolidayFormData = {
  name: '',
  date: '',
  end_date: '',
  description: '',
  is_recurring: false,
  is_active: true,
  isRange: false,
}

const PublicHolidaysPage = () => {
  const queryClient = useQueryClient()
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState(currentYear)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingHoliday, setEditingHoliday] = useState<PublicHoliday | null>(null)
  const [formData, setFormData] = useState<HolidayFormData>(emptyFormData)
  const [showInactive, setShowInactive] = useState(false)

  // Fetch holidays
  const { data, isLoading } = useQuery({
    queryKey: ['adminHolidays', selectedYear, showInactive],
    queryFn: () => leaveApi.getAdminHolidays({ year: selectedYear, include_inactive: showInactive }),
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreatePublicHolidayInput) => leaveApi.createHoliday(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminHolidays'] })
      queryClient.invalidateQueries({ queryKey: ['publicHolidays'] })
      toast.success('Public holiday created successfully')
      closeModal()
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || 'Failed to create public holiday'
      toast.error(message)
    },
  })

  // Bulk create mutation
  const bulkCreateMutation = useMutation({
    mutationFn: (data: BulkCreatePublicHolidayInput) => leaveApi.createHolidaysBulk(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['adminHolidays'] })
      queryClient.invalidateQueries({ queryKey: ['publicHolidays'] })
      toast.success(response.message)
      closeModal()
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || 'Failed to create public holidays'
      toast.error(message)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePublicHolidayInput }) =>
      leaveApi.updateHoliday(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminHolidays'] })
      queryClient.invalidateQueries({ queryKey: ['publicHolidays'] })
      toast.success('Public holiday updated successfully')
      closeModal()
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || 'Failed to update public holiday'
      toast.error(message)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => leaveApi.deleteHoliday(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminHolidays'] })
      queryClient.invalidateQueries({ queryKey: ['publicHolidays'] })
      toast.success('Public holiday deleted successfully')
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error?.message || 'Failed to delete public holiday'
      toast.error(message)
    },
  })

  const openCreateModal = () => {
    setEditingHoliday(null)
    setFormData(emptyFormData)
    setIsModalOpen(true)
  }

  const openEditModal = (holiday: PublicHoliday) => {
    setEditingHoliday(holiday)
    setFormData({
      name: holiday.name,
      date: holiday.date,
      end_date: '',
      description: holiday.description || '',
      is_recurring: holiday.is_recurring,
      is_active: holiday.is_active,
      isRange: false,
    })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingHoliday(null)
    setFormData(emptyFormData)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error('Name is required')
      return
    }

    if (!formData.date) {
      toast.error('Start date is required')
      return
    }

    if (formData.isRange && !formData.end_date) {
      toast.error('End date is required for date range')
      return
    }

    if (formData.isRange && formData.end_date < formData.date) {
      toast.error('End date must be on or after start date')
      return
    }

    if (editingHoliday) {
      updateMutation.mutate({
        id: editingHoliday.id,
        data: {
          name: formData.name,
          date: formData.date,
          description: formData.description || undefined,
          is_recurring: formData.is_recurring,
          is_active: formData.is_active,
        },
      })
    } else if (formData.isRange) {
      bulkCreateMutation.mutate({
        name: formData.name,
        start_date: formData.date,
        end_date: formData.end_date,
        description: formData.description || undefined,
        is_recurring: formData.is_recurring,
        is_active: formData.is_active,
      })
    } else {
      createMutation.mutate({
        name: formData.name,
        date: formData.date,
        description: formData.description || undefined,
        is_recurring: formData.is_recurring,
        is_active: formData.is_active,
      })
    }
  }

  const handleDelete = (holiday: PublicHoliday) => {
    if (window.confirm(`Are you sure you want to delete "${holiday.name}"?`)) {
      deleteMutation.mutate(holiday.id)
    }
  }

  const holidays = data?.data || []

  // Generate year options (5 years back, 2 years forward)
  const yearOptions = []
  for (let y = currentYear - 5; y <= currentYear + 2; y++) {
    yearOptions.push(y)
  }

  if (isLoading) {
    return <LoadingPage />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50/30">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-rose-700 px-6 py-8 shadow-lg">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white mb-1">Public Holidays</h1>
              <p className="text-red-200">Manage public holidays for leave calculation</p>
            </div>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-red-700 font-semibold rounded-xl hover:bg-red-50 transition-all shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Holiday
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Year:</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
              />
              <span className="text-sm text-gray-700">Show inactive</span>
            </label>

            <div className="ml-auto text-sm text-gray-500">
              {holidays.length} holiday{holidays.length !== 1 ? 's' : ''} found
            </div>
          </div>
        </div>

        {/* Holidays List */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {holidays.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No public holidays</h3>
              <p className="text-gray-500 mb-6">No public holidays found for {selectedYear}.</p>
              <button
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add First Holiday
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Recurring
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {holidays.map((holiday) => (
                    <tr key={holiday.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                            <span className="text-sm font-bold text-red-600">
                              {format(new Date(holiday.date), 'd')}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {format(new Date(holiday.date), 'EEEE')}
                            </div>
                            <div className="text-sm text-gray-500">
                              {format(new Date(holiday.date), 'MMM d, yyyy')}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{holiday.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600 max-w-xs truncate">
                          {holiday.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {holiday.is_recurring ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                            Yearly
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            holiday.is_active
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {holiday.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(holiday)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(holiday)}
                            className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-red-50 border border-red-100 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-red-900 mb-1">About Public Holidays</h4>
              <p className="text-sm text-red-700">
                Public holidays are automatically excluded when calculating leave days. When an employee requests leave that includes a public holiday, those days will not be counted against their leave balance.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" onClick={closeModal} />

            <div className="relative inline-block w-full max-w-lg bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8">
              <form onSubmit={handleSubmit}>
                <div className="px-6 py-5 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {editingHoliday ? 'Edit Public Holiday' : 'Add Public Holiday'}
                  </h3>
                </div>

                <div className="px-6 py-5 space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Holiday Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="e.g., New Year's Day"
                      required
                    />
                  </div>

                  {/* Date Range Toggle - Only show when creating */}
                  {!editingHoliday && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.isRange}
                          onChange={(e) => setFormData({ ...formData, isRange: e.target.checked, end_date: '' })}
                          className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Date range</span>
                      </label>
                      <span className="text-xs text-gray-500">
                        Create holidays for multiple consecutive days
                      </span>
                    </div>
                  )}

                  {/* Date Fields */}
                  <div className={formData.isRange && !editingHoliday ? 'grid grid-cols-2 gap-4' : ''}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {formData.isRange && !editingHoliday ? 'Start Date *' : 'Date *'}
                      </label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        required
                      />
                    </div>
                    {formData.isRange && !editingHoliday && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                          End Date *
                        </label>
                        <input
                          type="date"
                          value={formData.end_date}
                          onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                          min={formData.date}
                          className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                          required
                        />
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Optional description..."
                    />
                  </div>

                  {/* Checkboxes */}
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_recurring}
                        onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">Recurring yearly</span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">Active</span>
                    </label>
                  </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending || bulkCreateMutation.isPending}
                    className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {createMutation.isPending || updateMutation.isPending || bulkCreateMutation.isPending
                      ? 'Saving...'
                      : editingHoliday
                      ? 'Update Holiday'
                      : formData.isRange
                      ? 'Create Holidays'
                      : 'Create Holiday'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PublicHolidaysPage
